"use client"

import type React from "react"
import { useState, useMemo, useRef, useEffect, useCallback } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { X, Upload, Trash2, Loader2 } from "lucide-react"
import { CRM_API_BASE } from "@/lib/config"
import { httpJson } from "@/lib/http"

interface AddBusinessModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: (businessId: number) => void
  preselectedClientId?: number
  clients?: Client[]
}

interface Client {
  id: number
  name: string
  company_name?: string
  first_name?: string
  last_name?: string
}

const truncateFilename = (filename: string, maxLength: number = 30): string => {
  if (filename.length <= maxLength) {
    return filename
  }
  const lastDotIndex = filename.lastIndexOf(".")
  if (lastDotIndex === -1) {
    return filename.slice(0, maxLength - 3) + "..."
  }
  const nameWithoutExt = filename.slice(0, lastDotIndex)
  const extension = filename.slice(lastDotIndex)
  if (extension.length >= maxLength - 3) {
    return filename.slice(0, maxLength - 3) + "..."
  }
  const availableLength = maxLength - 3 - extension.length
  const startLength = Math.ceil(availableLength * 0.6)
  const endLength = Math.floor(availableLength * 0.4)
  const start = nameWithoutExt.slice(0, startLength)
  const end = nameWithoutExt.slice(-endLength)
  return `${start}...${end}${extension}`
}

export function AddBusinessModal({ isOpen, onClose, onSuccess, preselectedClientId, clients: clientsProp }: AddBusinessModalProps) {
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [clients, setClients] = useState<Client[]>(clientsProp || [])
  const [initialClients, setInitialClients] = useState<Client[]>(clientsProp || [])
  const [loadingClients, setLoadingClients] = useState(false)
  const [clientsLoaded, setClientsLoaded] = useState(!!(clientsProp && clientsProp.length > 0))
  const [clientAbortController, setClientAbortController] = useState<AbortController | null>(null)
  
  const [selectedClientId, setSelectedClientId] = useState<string>("")
  const [clientSearchTerm, setClientSearchTerm] = useState("")
  const [showClientDropdown, setShowClientDropdown] = useState(false)
  
  const [business, setBusiness] = useState({
    name: "",
    domain: "",
    industry: "",
    about: "",
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    contact_job_title: "",
    social_media: "",
    target_audience: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip: "",
    logo: null as File | null,
  })
  
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState("")
  const [errorFields, setErrorFields] = useState<string[]>([])
  
  const [industryOptions, setIndustryOptions] = useState<Array<{ id: number; text: string }>>([])
  const [loadingIndustries, setLoadingIndustries] = useState(true)

  useEffect(() => {
    if (isOpen && preselectedClientId) {
      setSelectedClientId(preselectedClientId.toString())
      loadPreselectedClient()
    }
  }, [isOpen, preselectedClientId])

  useEffect(() => {
    if (clientsProp && clientsProp.length > 0) {
      setClients(clientsProp)
    }
  }, [clientsProp])

  useEffect(() => {
    if (!isOpen) {
      setBusiness({
        name: "",
        domain: "",
        industry: "",
        about: "",
        contact_name: "",
        contact_phone: "",
        contact_email: "",
        contact_job_title: "",
        social_media: "",
        target_audience: "",
        address_line1: "",
        address_line2: "",
        city: "",
        state: "",
        zip: "",
        logo: null,
      })
      setIndustryOptions([])
      setLoadingIndustries(true)
      setSelectedClientId(preselectedClientId ? preselectedClientId.toString() : "")
      setClientSearchTerm("")
      setShowClientDropdown(false)
      setLogoPreview(null)
      setError("")
      setErrorFields([])
      setClientsLoaded(false)
      if (clientAbortController) {
        clientAbortController.abort()
        setClientAbortController(null)
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }
  }, [isOpen, preselectedClientId])

  useEffect(() => {
    if (isOpen && !clientsLoaded) {
      fetchClients()
    }
  }, [isOpen])

  useEffect(() => {
    const fetchIndustries = async () => {
      if (!isOpen) return
      try {
        setLoadingIndustries(true)
        const allIndustries: Array<{ id: number; text: string }> = []
        let offset = 0
        const limit = 100
        let hasMore = true
        
        while (hasMore) {
          const url = `${CRM_API_BASE}/industry/?limit=${limit}&offset=${offset}`
          const response = await httpJson<any>(url)
          
          let rawIndustries: Array<{ id: number; text: string }> = []
          if (Array.isArray(response)) {
            rawIndustries = response
          } else if (response.results && Array.isArray(response.results)) {
            rawIndustries = response.results
          } else if (response.data && Array.isArray(response.data)) {
            rawIndustries = response.data
          }
          
          const industries: Array<{ id: number; text: string }> = rawIndustries.map((ind) => ({
            id: ind.id,
            text: ind.text || `Industry ${ind.id}`,
          }))
          
          allIndustries.push(...industries)
          
          const totalCount = response.count || response.total || industries.length
          if (industries.length < limit || allIndustries.length >= totalCount) {
            hasMore = false
          } else {
            offset += limit
          }
        }
        
        setIndustryOptions(allIndustries)
      } catch (error) {
        if (error instanceof Error && (error.message.includes("Request was cancelled") || error.name === "AbortError")) {
          return
        }
        console.error("Error fetching industries:", error)
        setIndustryOptions([])
      } finally {
        setLoadingIndustries(false)
      }
    }

    if (isOpen) {
      fetchIndustries()
    }
  }, [isOpen])

  useEffect(() => {
    if (clientAbortController) {
      clientAbortController.abort()
    }

    if (clientSearchTerm.trim().length < 2) {
      if (initialClients.length > 0) {
        setClients([...initialClients])
      } else if (clientsProp && clientsProp.length > 0) {
        setClients([...clientsProp])
        setInitialClients([...clientsProp])
      }
      setLoadingClients(false)
      return
    }

    const newAbortController = new AbortController()
    setClientAbortController(newAbortController)

    const timeoutId = setTimeout(() => {
      fetchClients(clientSearchTerm, newAbortController)
    }, 300)

    return () => {
      clearTimeout(timeoutId)
      if (newAbortController) {
        newAbortController.abort()
      }
    }
  }, [clientSearchTerm])

  const loadPreselectedClient = useCallback(async () => {
    if (!preselectedClientId) return
    try {
      const response = await httpJson(`${CRM_API_BASE}/client/${preselectedClientId}/?include_archived=true`)
      if (response) {
        setClients([response])
      }
    } catch (error) {
      console.error("Error loading preselected client:", error)
    }
  }, [preselectedClientId])

  const fetchClients = async (searchTerm = "", abortController?: AbortController) => {
    try {
      setLoadingClients(true)

      const url = searchTerm
        ? `${CRM_API_BASE}/client/?q=${encodeURIComponent(searchTerm)}&limit=20`
        : `${CRM_API_BASE}/client/?limit=20`

      const response = await httpJson<{ results?: Client[] }>(url, {
        signal: abortController?.signal,
      })

      if (response.results) {
        if (searchTerm) {
          setClients(response.results)
        } else {
          setInitialClients(response.results)
          setClients(response.results)
        }
        setClientsLoaded(true)
      } else {
        if (!searchTerm) {
          setInitialClients([])
        }
        setClients([])
        setClientsLoaded(true)
      }
    } catch (error) {
      if (error instanceof Error && (error.message.includes("Request was cancelled") || error.name === "AbortError")) {
        return
      }
      console.error("Error fetching clients:", error)
      setClients([])
    } finally {
      setLoadingClients(false)
      if (abortController) {
        setClientAbortController(null)
      }
    }
  }

  const formValidation = useMemo(() => {
    const requiredFields: Record<string, string | undefined> = {
      name: business.name?.trim(),
      contact_name: business.contact_name?.trim(),
      contact_email: business.contact_email?.trim(),
    }

    const hasAnyAddressField = business.address_line1?.trim() || 
                              business.address_line2?.trim() || 
                              business.city?.trim() || 
                              business.state?.trim() || 
                              business.zip?.trim()
    
    if (hasAnyAddressField) {
      if (!business.address_line1?.trim()) requiredFields.address_line1 = ""
      if (!business.city?.trim()) requiredFields.city = ""
      if (!business.state?.trim()) requiredFields.state = ""
      if (!business.zip?.trim()) requiredFields.zip = ""
    }

    const missingFields = Object.entries(requiredFields)
      .filter(([_, value]) => !value)
      .map(([field, _]) => field)

    const isValid = missingFields.length === 0

    return { isValid, missingFields }
  }, [business])

  const isFormValid = formValidation.isValid

  const hasAnyAddressField = business.address_line1?.trim() || 
                            business.address_line2?.trim() || 
                            business.city?.trim() || 
                            business.state?.trim() || 
                            business.zip?.trim()

  const handleChange = (field: string, value: string | File | null) => {
    setBusiness({ ...business, [field]: value })
    if (error) {
      setError("")
    }
    if (errorFields.length > 0) {
      setErrorFields([])
    }
  }

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    
    if (file) {
      if (!file.type.startsWith("image/")) {
        setError("Please select an image file")
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        setError("Image size must be less than 5MB")
        return
      }
      const reader = new FileReader()
      reader.onloadend = () => {
        setLogoPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
      handleChange("logo", file)
    } else {
      handleChange("logo", null)
      setLogoPreview(null)
    }
  }

  const handleRemoveLogo = () => {
    handleChange("logo", null)
    setLogoPreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const getClientDisplayName = (client: Client) => {
    if (client.company_name) return client.company_name
    if (client.name) return client.name
    const fullName = `${client.first_name || ""} ${client.last_name || ""}`.trim()
    return fullName || `Client ${client.id}`
  }

  const selectedClient = useMemo(() => {
    if (!selectedClientId) return null
    return clients.find((client) => client.id.toString() === selectedClientId) ||
           initialClients.find((client) => client.id.toString() === selectedClientId) ||
           null
  }, [selectedClientId, clients, initialClients])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (loading) return

    if (!selectedClientId && !preselectedClientId) {
      toast({
        title: "Validation Error",
        description: "Please select a client.",
        variant: "destructive",
      })
      return
    }

    if (!formValidation.isValid) {
      setError("Please fill in all required fields")
      setErrorFields(formValidation.missingFields)
      return
    }

    setLoading(true)
    setError("")
    setErrorFields([])

    try {
      const hasAllRequiredAddressFields = business.address_line1?.trim() && 
                                         business.city?.trim() && 
                                         business.state?.trim() && 
                                         business.zip?.trim()
      
      let addressId: number | null = null
      
      if (hasAnyAddressField && hasAllRequiredAddressFields) {
        const addressData = {
          line_1: business.address_line1.trim(),
          ...(business.address_line2?.trim() && { line_2: business.address_line2.trim() }),
          city: business.city.trim(),
          state: business.state.trim(),
          zip_code: business.zip.trim(),
          reference: "Business Address",
        }

        const addressResponse = await httpJson(`${CRM_API_BASE}/address/`, {
          method: "POST",
          body: JSON.stringify(addressData),
        })

        addressId = addressResponse.id
      }

      // Generate created_at in UTC format: 2025-10-27 15:58:19.830000
      const now = new Date()
      const year = now.getUTCFullYear()
      const month = String(now.getUTCMonth() + 1).padStart(2, '0')
      const day = String(now.getUTCDate()).padStart(2, '0')
      const hours = String(now.getUTCHours()).padStart(2, '0')
      const minutes = String(now.getUTCMinutes()).padStart(2, '0')
      const seconds = String(now.getUTCSeconds()).padStart(2, '0')
      const milliseconds = String(now.getUTCMilliseconds()).padStart(3, '0')
      const microseconds = milliseconds.padEnd(6, '0') // Convert to 6 digits (microseconds)
      const created_at = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${microseconds}`

      const businessData: any = {
        client: preselectedClientId ? preselectedClientId : Number.parseInt(selectedClientId),
        name: business.name.trim(),
        urls: [],
        created_at: created_at,
      }

      if (business.domain?.trim()) {
        businessData.domain = business.domain.trim()
      }
      if (business.about?.trim()) {
        businessData.about = business.about.trim()
      }
      if (business.contact_name?.trim()) {
        businessData.contact_name = business.contact_name.trim()
      }
      if (business.contact_phone?.trim()) {
        businessData.contact_phone = business.contact_phone.trim()
      }
      if (business.contact_email?.trim()) {
        businessData.contact_email = business.contact_email.trim()
      }
      if (business.contact_job_title?.trim()) {
        businessData.contact_job_title = business.contact_job_title.trim()
      }
      if (business.social_media?.trim()) {
        businessData.social_media = business.social_media.trim()
      }
      if (business.target_audience?.trim()) {
        businessData.target_audience = business.target_audience.trim()
      }
      if (business.industry) {
        businessData.industries = [Number.parseInt(business.industry)]
      }
      if (addressId) {
        businessData.addresses = [addressId]
      }

      const token = typeof window !== "undefined" ? localStorage.getItem("auth_token") : null
      if (!token) {
        throw new Error("Authentication token not found")
      }

      const response = await httpJson(`${CRM_API_BASE}/business/`, {
        method: "POST",
        body: JSON.stringify(businessData),
      })

      if (business.logo) {
        const logoFormData = new FormData()
        logoFormData.append("logo", business.logo)

        const logoResponse = await fetch(`${CRM_API_BASE}/business/${response.id}/`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: logoFormData,
        })

        if (!logoResponse.ok) {
          const errorText = await logoResponse.text()
          let errorMessage = `HTTP ${logoResponse.status}: Failed to upload logo`
          
          try {
            const errorData = JSON.parse(errorText)
            errorMessage = errorData.detail || errorData.error || errorData.logo?.[0] || errorMessage
          } catch {
            errorMessage = errorText.slice(0, 200) || errorMessage
          }

          toast({
            title: "Warning",
            description: `Business created successfully, but logo upload failed: ${errorMessage}`,
            variant: "destructive",
          })
        }
      }

      toast({
        title: "Success",
        description: "Business created successfully!",
      })
      onSuccess?.(response.id)
      onClose()
    } catch (error: any) {
      console.error("Error creating business:", error)
      if (error.message) {
        const errorMessage = error.message.toLowerCase()
        const errorFields: string[] = []
        let errorText = ""

        if (errorMessage.includes("email") || errorMessage.includes("contact_email")) {
          errorFields.push("contact_email")
          errorText += "This email already exists. Please use another email. "
        }

        if (errorFields.length > 0) {
          setErrorFields(errorFields)
          setError(errorText.trim())
          setTimeout(() => {
            if (errorFields.includes("contact_email")) {
              const emailField = document.getElementById("contact_email")
              if (emailField) {
                emailField.focus()
                emailField.scrollIntoView({ behavior: "smooth", block: "center" })
              }
            }
          }, 100)
          return
        }
      }

      setError("Failed to create business. Please check your information and try again.")
      setTimeout(() => {
        const modalContent = document.querySelector(".bg-background.rounded-lg.shadow-lg")
        if (modalContent) {
          modalContent.scrollTop = 0
        }
      }, 100)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Add New Business</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <button
            type="button"
            style={{
              position: "absolute",
              width: "1px",
              height: "1px",
              padding: 0,
              margin: "-1px",
              overflow: "hidden",
              clip: "rect(0, 0, 0, 0)",
              whiteSpace: "nowrap",
              border: 0,
            }}
            autoFocus
            tabIndex={0}
            aria-hidden="true"
          />

          <div className="grid gap-6">
            <div className="border border-border rounded-lg p-4">
              <div className="space-y-2">
                <Label htmlFor="client">
                  Client <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <div className="relative">
                    <Input
                      id="client"
                      placeholder={
                        loadingClients
                          ? "Loading clients..."
                          : selectedClient && !clientSearchTerm
                            ? ""
                            : "Search clients..."
                      }
                      value={
                        preselectedClientId && selectedClient
                          ? getClientDisplayName(selectedClient)
                          : selectedClient && !clientSearchTerm
                            ? getClientDisplayName(selectedClient)
                            : clientSearchTerm
                      }
                      onMouseDown={(e) => {
                        if (preselectedClientId) return
                        e.preventDefault()
                        setShowClientDropdown(true)
                        setClientSearchTerm("")
                        if (!clientsLoaded) {
                          fetchClients()
                        }
                        setTimeout(() => {
                          ;(e.target as HTMLInputElement).focus()
                        }, 0)
                      }}
                      onFocus={() => {
                        if (preselectedClientId) return
                        if (!showClientDropdown && !clientsLoaded) {
                          fetchClients()
                        }
                      }}
                      onChange={(e) => {
                        if (preselectedClientId) return
                        const searchValue = e.target.value
                        setClientSearchTerm(searchValue)
                        setShowClientDropdown(true)

                        if (selectedClient) {
                          setSelectedClientId("")
                        }

                        if (!searchValue.trim() || searchValue.trim().length < 2) {
                          if (clientAbortController) {
                            clientAbortController.abort()
                            setClientAbortController(null)
                          }

                          let clientsToShow = [...initialClients]
                          if (selectedClientId) {
                            const currentClient = clients.find((client) => client.id.toString() === selectedClientId)
                            if (currentClient && !clientsToShow.some((client) => client.id === currentClient.id)) {
                              clientsToShow = [currentClient, ...clientsToShow]
                            }
                          }

                          setClients(clientsToShow)
                          setLoadingClients(false)
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => setShowClientDropdown(false), 200)
                      }}
                      disabled={!!preselectedClientId || loadingClients}
                      className={`border-gray-400 focus:border-primary focus:ring-0 pr-20 h-9 w-full ${errorFields.includes("client") ? "border-red-500 focus:border-red-500 focus:ring-red-200 bg-red-50" : ""}`}
                    />
                  {!preselectedClientId && (clientSearchTerm || selectedClient) && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setClientSearchTerm("")
                        setSelectedClientId("")
                        setShowClientDropdown(true)
                      }}
                      className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded p-1 z-20"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                  {!preselectedClientId && (
                    <button
                      type="button"
                      onClick={() => {
                        if (clients.length > 0 && !showClientDropdown) {
                          setShowClientDropdown(true)
                          setClientSearchTerm("")
                        } else if (showClientDropdown) {
                          setShowClientDropdown(false)
                        }
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer hover:text-gray-600 p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed z-10"
                      disabled={loadingClients || clients.length === 0}
                    >
                      <svg className={`w-4 h-4 transition-transform duration-200 ${showClientDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}

                  </div>
                  {!preselectedClientId && showClientDropdown && (
                    <div
                      className="absolute z-[9999] mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto scrollbar-minimal"
                      style={{ left: "0", width: "100%" }}
                    >
                      {clientSearchTerm.trim().length > 0 && clientSearchTerm.trim().length < 2 ? (
                        <div className="p-3 text-sm text-gray-600">Type at least 2 characters to search...</div>
                      ) : loadingClients ? (
                        <div className="p-3 text-sm text-gray-600">Searching clients...</div>
                      ) : clients.length === 0 ? (
                        <div className="p-3 text-sm text-gray-600">
                          {clientSearchTerm.trim()
                            ? `No clients found for "${clientSearchTerm}"`
                            : "No clients available"}
                        </div>
                      ) : (
                        <div>
                          {(() => {
                            const clientsToShow = clientSearchTerm.trim() === ""
                              ? initialClients
                              : clients.filter((client) => {
                                  const clientName = getClientDisplayName(client)
                                  return clientName.toLowerCase().includes(clientSearchTerm.toLowerCase())
                                })
                            return (
                              <>
                                {clientsToShow.map((client) => (
                                  <div
                                    key={client.id}
                                    className="px-4 py-3 hover:bg-blue-50 cursor-pointer text-sm text-gray-900 border-b border-gray-100 last:border-b-0"
                                    onClick={() => {
                                      setSelectedClientId(client.id.toString())
                                      setClientSearchTerm("")
                                      setShowClientDropdown(false)
                                      if (!initialClients.find((c) => c.id === client.id)) {
                                        setInitialClients([client, ...initialClients])
                                      }
                                      if (!clients.find((c) => c.id === client.id)) {
                                        setClients([client, ...clients])
                                      }
                                    }}
                                  >
                                    {getClientDisplayName(client)}
                                  </div>
                                ))}
                                {((clientSearchTerm.trim() === "" ? initialClients.length : clients.length) >= 10) && (
                                  <div className="p-2 text-xs text-muted-foreground border-t bg-gray-50">
                                    💡 Type a client name to search for more results
                                  </div>
                                )}
                              </>
                            )
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="border border-border rounded-lg p-4">
              <h3 className="text-sm font-semibold text-foreground mb-4">Business Information</h3>

              <div className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="domain">Domain</Label>
                  <Input
                    id="domain"
                    value={business.domain}
                    onChange={(e) => handleChange("domain", e.target.value)}
                    maxLength={255}
                    placeholder="example.com"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">
                      Business Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      value={business.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      maxLength={255}
                      required
                      placeholder="Enter business name"
                      className={errorFields.includes("name") ? "border-red-500" : ""}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="logo">Business Logo</Label>
                    <div className="flex gap-2 items-center min-w-0 overflow-hidden">
                      <Input
                        ref={fileInputRef}
                        id="logo"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="hidden"
                      />
                      {!business.logo ? (
                        <Button
                          type="button"
                          variant="default"
                          size="default"
                          onClick={() => fileInputRef.current?.click()}
                          className="flex items-center gap-2 shrink-0"
                        >
                          <Upload className="h-4 w-4" />
                          Choose File
                        </Button>
                      ) : (
                        <>
                          {logoPreview && (
                            <img
                              src={logoPreview}
                              alt="Logo preview"
                              className="h-8 w-8 object-contain rounded border border-border shrink-0"
                            />
                          )}
                          <span className="text-sm text-muted-foreground flex-1 min-w-0 overflow-hidden" title={business.logo.name}>
                            {truncateFilename(business.logo.name)}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="default"
                            onClick={handleRemoveLogo}
                            className="shrink-0 text-destructive hover:text-red-700 hover:bg-transparent"
                          >
                            <Trash2 className="h-4 w-4 transition-transform hover:scale-110" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div></div>
                  <div className="-mt-2">
                    <p className="text-xs text-gray-500">Upload business logo (PNG, JPG, or SVG, max 5MB)</p>
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Select
                    value={business.industry}
                    onValueChange={(value) => handleChange("industry", value)}
                    disabled={loadingIndustries}
                  >
                    <SelectTrigger className="border-2 border-gray-300">
                      <SelectValue placeholder={loadingIndustries ? "Loading industries..." : "Select industry"}>
                        {business.industry && industryOptions.length > 0
                          ? industryOptions.find((ind) => ind.id.toString() === business.industry)?.text || "Select industry"
                          : "Select industry"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {loadingIndustries ? (
                        <SelectItem value="loading" disabled>Loading industries...</SelectItem>
                      ) : industryOptions.length > 0 ? (
                        industryOptions.map((industry) => (
                          <SelectItem key={industry.id} value={industry.id.toString()}>
                            {industry.text}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-industries" disabled>No industries available</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="pt-6"></div>

                <div className="grid gap-2">
                  <Label htmlFor="address_line1">
                    Address Line 1<span className="text-red-500"> *</span>
                  </Label>
                  <Input
                    id="address_line1"
                    value={business.address_line1}
                    onChange={(e) => handleChange("address_line1", e.target.value)}
                    maxLength={255}
                    placeholder="Street address"
                    className={errorFields.includes("address_line1") ? "border-red-500" : ""}
                  />
                  {errorFields.includes("address_line1") && (
                    <p className="text-xs text-red-500">Address Line 1 is required when providing address information</p>
                  )}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="address_line2">Address Line 2 (optional)</Label>
                  <Input
                    id="address_line2"
                    value={business.address_line2}
                    onChange={(e) => handleChange("address_line2", e.target.value)}
                    maxLength={255}
                    placeholder="Apartment, suite, etc. (optional)"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="city">
                      City<span className="text-red-500"> *</span>
                    </Label>
                    <Input
                      id="city"
                      value={business.city}
                      onChange={(e) => handleChange("city", e.target.value)}
                      maxLength={100}
                      placeholder="City"
                      className={errorFields.includes("city") ? "border-red-500" : ""}
                    />
                    {errorFields.includes("city") && (
                      <p className="text-xs text-red-500">City is required when providing address information</p>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="state">
                      State<span className="text-red-500"> *</span>
                    </Label>
                    <Input
                      id="state"
                      value={business.state}
                      onChange={(e) => handleChange("state", e.target.value)}
                      maxLength={50}
                      placeholder="State"
                      className={errorFields.includes("state") ? "border-red-500" : ""}
                    />
                    {errorFields.includes("state") && (
                      <p className="text-xs text-red-500">State is required when providing address information</p>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="zip">
                      ZIP Code<span className="text-red-500"> *</span>
                    </Label>
                    <Input
                      id="zip"
                      value={business.zip}
                      onChange={(e) => handleChange("zip", e.target.value)}
                      maxLength={20}
                      placeholder="ZIP"
                      className={errorFields.includes("zip") ? "border-red-500" : ""}
                    />
                    {errorFields.includes("zip") && (
                      <p className="text-xs text-red-500">ZIP Code is required when providing address information</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="border border-border rounded-lg p-4">
              <h3 className="text-sm font-semibold text-foreground mb-4">Primary Contact</h3>

              <div className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="contact_name">
                    Contact Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="contact_name"
                    value={business.contact_name}
                    onChange={(e) => handleChange("contact_name", e.target.value)}
                    maxLength={255}
                    placeholder="John Doe"
                    className={errorFields.includes("contact_name") ? "border-red-500" : ""}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="contact_email">
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={business.contact_email}
                    onChange={(e) => handleChange("contact_email", e.target.value)}
                    maxLength={255}
                    placeholder="email@example.com"
                    className={errorFields.includes("contact_email") ? "border-red-500" : ""}
                  />
                  {errorFields.includes("contact_email") && (
                    <p className="text-xs text-red-500">
                      This email already exists. Please use another email.
                    </p>
                  )}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="contact_phone">Phone</Label>
                  <Input
                    id="contact_phone"
                    value={business.contact_phone}
                    onChange={(e) => handleChange("contact_phone", e.target.value)}
                    maxLength={255}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6 pt-6 border-t">
            <Button variant="outline" type="button" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !isFormValid || (!selectedClientId && !preselectedClientId)}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Business...
                </>
              ) : (
                "Save Business"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
